import * as React from "react";
import WDProvince from "./WDProvince";
import WDProvinceBorderHighlight from "./WDProvinceBorderHighlight";
import WDProvinceOverlay from "./WDProvinceOverlay";
import { Unit } from "../../../utils/map/getUnits";
import {
  gameData,
  gameLegalOrders,
  gameMaps,
  gameOrder,
  gameOverview,
} from "../../../state/game/game-api-slice";
import { useAppSelector } from "../../../state/hooks";
import { IProvinceStatus } from "../../../models/Interfaces";
import OrderState from "../../../state/interfaces/OrderState";
import { LegalOrders } from "../../../utils/state/gameApiSlice/extraReducers/fetchGameData/precomputeLegalOrders";
import type { BoardMapProps } from "../../../map/variants";

/*
通用地图底图组件：纹理 + 省份层。
它不直接 import 任何地图数据——省份几何/映射/渲染顺序全部通过 props 传入，
因此任何地图变体都可以复用它（classic.ts 与生成的新地图都指向这个组件）。
*/
const WDBoardMap: React.FC<BoardMapProps> = function ({
  units,
  centersByProvince,
  phase,
  isLivePhase,
  provincesMapData,
  territoryMap,
  provincesToRenderLast = [],
}): React.ReactElement {
  const gameDataResponse = useAppSelector(gameData);
  const maps = useAppSelector(gameMaps);
  const provinceStatusByProvID: { [key: string]: IProvinceStatus } = {};
  gameDataResponse.data.territoryStatuses.forEach((provinceStatus) => {
    provinceStatusByProvID[maps.terrIDToProvince[provinceStatus.id]] =
      provinceStatus;
  });

  const curOrder: OrderState = useAppSelector(gameOrder);
  const legalOrders: LegalOrders = useAppSelector(gameLegalOrders);

  const overview = useAppSelector(gameOverview);
  const { members, user } = overview;

  let provinceCountryHighlightByProvince: { [key: string]: string } = {};
  let provincesToChoose: string[] = [];
  if (isLivePhase && user) {
    if (phase === "Diplomacy") {
      if (!curOrder.inProgress) {
        provinceCountryHighlightByProvince = {};
        provincesToChoose = [];
      } else if (curOrder.type === "Move") {
        if (curOrder.viaConvoy) {
          provinceCountryHighlightByProvince[
            maps.terrIDToProvince[maps.unitToTerrID[curOrder.unitID]]
          ] = curOrder.countryID;
          provincesToChoose = legalOrders.legalViasByUnitID[
            curOrder.unitID
          ].map((via) => territoryMap[via.dest].province);
        } else {
          provinceCountryHighlightByProvince[
            maps.terrIDToProvince[maps.unitToTerrID[curOrder.unitID]]
          ] = curOrder.countryID;
          provincesToChoose = legalOrders.legalMoveDestsByUnitID[
            curOrder.unitID
          ].map((territory) => territoryMap[territory].province);
        }
      } else if (curOrder.type === "Support") {
        if (curOrder.fromTerrID) {
          provinceCountryHighlightByProvince[
            maps.terrIDToProvince[maps.unitToTerrID[curOrder.unitID]]
          ] = curOrder.countryID;
          provinceCountryHighlightByProvince[
            maps.terrIDToProvince[curOrder.fromTerrID]
          ] = curOrder.countryID;
          provincesToChoose = legalOrders.legalSupportsByUnitID[
            curOrder.unitID
          ][maps.terrIDToProvince[curOrder.fromTerrID]].map(
            (support) => support.dest,
          );
        } else {
          provinceCountryHighlightByProvince[
            maps.terrIDToProvince[maps.unitToTerrID[curOrder.unitID]]
          ] = curOrder.countryID;
          provincesToChoose = Object.keys(
            legalOrders.legalSupportsByUnitID[curOrder.unitID],
          ) as string[];
        }
      } else if (curOrder.type === "Convoy") {
        if (curOrder.fromTerrID) {
          provinceCountryHighlightByProvince[
            maps.terrIDToProvince[maps.unitToTerrID[curOrder.unitID]]
          ] = curOrder.countryID;
          provinceCountryHighlightByProvince[
            maps.terrIDToProvince[curOrder.fromTerrID]
          ] = curOrder.countryID;
          provincesToChoose = Object.keys(
            legalOrders.legalConvoysByUnitID[curOrder.unitID][
              maps.terrIDToProvince[curOrder.fromTerrID]
            ],
          ) as string[];
        } else {
          provinceCountryHighlightByProvince[
            maps.terrIDToProvince[maps.unitToTerrID[curOrder.unitID]]
          ] = curOrder.countryID;
          provincesToChoose = Object.keys(
            legalOrders.legalConvoysByUnitID[curOrder.unitID],
          ) as string[];
        }
      }
    } else if (phase === "Retreats") {
      if (!curOrder.inProgress) {
        provinceCountryHighlightByProvince = {};
        provincesToChoose = Object.keys(
          legalOrders.legalRetreatDestsByUnitID,
        ).map((unitID) => maps.terrIDToProvince[maps.unitToTerrID[unitID]]);
      } else if (curOrder.type === "Retreat") {
        provinceCountryHighlightByProvince[
          maps.terrIDToProvince[maps.unitToTerrID[curOrder.unitID]]
        ] = curOrder.countryID;
        provincesToChoose = legalOrders.legalRetreatDestsByUnitID[
          curOrder.unitID
        ].map((territory) => territoryMap[territory].province);
        provincesToChoose.push(
          maps.terrIDToProvince[maps.unitToTerrID[curOrder.unitID]],
        );
      }
    } else if (phase === "Builds") {
      if (
        gameDataResponse.data.isSandboxMode ||
        user.member.supplyCenterNo < user.member.unitNo
      ) {
        provincesToChoose = provincesToChoose.concat(
          units
            .filter((unit) =>
              Object.keys(legalOrders.legalDestroyDestsByUnitID).includes(
                unit.unit.id,
              ),
            )
            .map((unit) => unit.mappedTerritory.province),
        );
      }
      if (
        gameDataResponse.data.isSandboxMode ||
        user.member.supplyCenterNo > user.member.unitNo
      ) {
        provincesToChoose = provincesToChoose.concat(
          legalOrders.possibleBuildDests.map(
            (territory) => territoryMap[territory].province,
          ),
        );
      }
    }
  }
  const provincesToHighlightSet = new Set(
    Object.keys(provinceCountryHighlightByProvince),
  );
  const provincesToChooseSet = new Set(provincesToChoose);

  const unplayableProvinces = Object.values(provincesMapData)
    .filter((data) => !data.playable)
    .map((data) => {
      return (
        <WDProvince
          provinceMapData={data}
          ownerCountryID={centersByProvince[data.province]?.ownerCountryID}
          playerCountryID={user?.member.countryID}
          highlightSelection={false}
          key={`${data.province}-province`}
        />
      );
    });

  // 由地图配置指定需要最后渲染的省份（例如标签会与邻接省份重叠的省份）。
  const playableProvincesData = Object.values(provincesMapData).filter(
    (data) => data.playable && !provincesToRenderLast.includes(data.province),
  );
  provincesToRenderLast.forEach((province) => {
    if (provincesMapData[province]) {
      playableProvincesData.push(provincesMapData[province]);
    }
  });

  const playableProvinces = playableProvincesData.map((data) => {
    const highlightSelection = provincesToHighlightSet.has(data.province);
    const highlightCountryID =
      provinceCountryHighlightByProvince[data.province];
    return (
      <WDProvince
        provinceMapData={data}
        ownerCountryID={centersByProvince[data.province]?.ownerCountryID}
        playerCountryID={user?.member.countryID}
        highlightSelection={highlightSelection}
        key={`${data.province}-province`}
      />
    );
  });

  const playableProvinceBorderHighlights = playableProvincesData
    .filter((data) => provincesToChooseSet.has(data.province))
    .map((data) => {
      return (
        <WDProvinceBorderHighlight
          provinceMapData={data}
          key={`${data.province}-province-border-highlight`}
        />
      );
    });

  const playableProvinceOverlays = playableProvincesData.map((data) => {
    return (
      <WDProvinceOverlay
        provinceMapData={data}
        units={units}
        key={`${data.province}-province-overlay`}
      />
    );
  });

  return (
    <g id="wD-boardmap-v10.3.4 1">
      <g id="unplayable">{unplayableProvinces}</g>
      <g id="playableProvinces">{playableProvinces}</g>
      <g id="playableProvinceBorderHighlights">
        {playableProvinceBorderHighlights}
      </g>
      <g id="playableProvinceOverlays">{playableProvinceOverlays}</g>
    </g>
  );
};

export default WDBoardMap;
